import React from "react";
import { AlertTriangle, ArrowLeft, ArrowRight, Briefcase, CalendarX, Palmtree } from "lucide-react";

/**
 * OPS-006A · AvailabilityWarningDialog — consolidates every conflict
 * detected for a proposed appointment window into ONE dialog:
 *
 *   • existing_appointment  – engineer already booked
 *   • non_working_day       – appointment outside their working days
 *   • holiday               – engineer on approved leave that date
 *
 * The dialog is advisory. Operations may:
 *   • "Change booking"    → onCancel — performs NO write
 *   • "Continue anyway"   → onOverride — parent re-submits with the
 *                           appropriate override flags. The parent is
 *                           responsible for guarding against double-
 *                           submits (see CalendarAppointmentForm's
 *                           `saving` guard).
 *
 * Warning objects follow the shape returned by
 * `POST /api/calendar/check-availability`.
 */

function warningIcon(type) {
    if (type === "existing_appointment") return Briefcase;
    if (type === "holiday") return Palmtree;
    return CalendarX;
}

function humanReason(w) {
    if (w.reason) return w.reason;
    if (w.type === "existing_appointment") {
        const who = w.engineer_name || "This engineer";
        const what = [w.customer_name, w.appointment_type].filter(Boolean).join(" — ");
        return `${who} is already booked${what ? `: ${what}` : ""}.`;
    }
    return `${w.engineer_name || "This engineer"} may be unavailable.`;
}

/**
 * Render the operator-entered wall-clock time. Always formatted in
 * the BROWSER's locale/timezone so entered 10:00–12:00 renders as
 * 10:00–12:00 regardless of how the backend serialised the ISO
 * string. Falls back to any `range` / `arrival_window` field the
 * backend may have supplied (kept for callers not migrated yet).
 */
function whenLabel(w) {
    if (w.start_at && w.end_at) {
        try {
            const s = new Date(w.start_at);
            const e = new Date(w.end_at);
            const sameDay = s.toDateString() === e.toDateString();
            const dOpts = { day: "numeric", month: "short", year: "numeric", weekday: "short" };
            const tOpts = { hour: "2-digit", minute: "2-digit", hour12: false };
            const d1 = s.toLocaleDateString(undefined, dOpts);
            const t1 = s.toLocaleTimeString(undefined, tOpts);
            const t2 = e.toLocaleTimeString(undefined, tOpts);
            if (sameDay) return `${d1}, ${t1}–${t2}`;
            const d2 = e.toLocaleDateString(undefined, dOpts);
            return `${d1} ${t1} – ${d2} ${t2}`;
        } catch { /* fall through */ }
    }
    return w.range || w.arrival_window || null;
}

function typeLabel(type) {
    if (type === "existing_appointment") return "Existing appointment";
    if (type === "non_working_day") return "Non-working day";
    if (type === "holiday") return "On leave";
    return "Unavailable";
}

export default function AvailabilityWarningDialog({ open, warnings = [], onCancel, onOverride }) {
    if (!open) return null;

    // Consolidate: sort clashes first, then holidays, then non-working days.
    const orderKey = { existing_appointment: 0, holiday: 1, non_working_day: 2 };
    const rows = [...warnings].sort(
        (a, b) => (orderKey[a.type] ?? 9) - (orderKey[b.type] ?? 9),
    );

    return (
        <div
            className="fixed inset-0 z-[60] bg-black/55 flex items-center justify-center px-4"
            data-testid="availability-warning-dialog"
            role="alertdialog"
            aria-labelledby="availability-warning-title"
        >
            <div className="bg-white w-full max-w-lg rounded-2xl shadow-xl overflow-hidden max-h-[92vh] flex flex-col">
                <div className="px-6 py-5 border-b border-[#E5E7EB] flex items-start gap-3">
                    <div className="w-10 h-10 rounded-full bg-[#DA3535]/12 text-[#DA3535] flex items-center justify-center shrink-0">
                        <AlertTriangle size={18} strokeWidth={1.75} />
                    </div>
                    <div className="flex-1 min-w-0">
                        <h3 id="availability-warning-title" className="text-lg font-semibold text-[#131B2F]">
                            Availability warning
                        </h3>
                        <p className="text-sm text-[#4B5565] mt-1">
                            {rows.length === 1
                                ? "One engineer may be unavailable for this booking."
                                : `${rows.length} conflicts detected for this booking.`}
                        </p>
                    </div>
                </div>

                <div className="px-6 py-4 overflow-y-auto flex-1 space-y-3">
                    {rows.map((w, i) => {
                        const Icon = warningIcon(w.type);
                        const when = whenLabel(w);
                        return (
                            <div
                                key={`${w.type}-${w.engineer_id || "x"}-${w.date || w.appointment_id || i}`}
                                className="border border-[#DA3535]/25 bg-[#FEF3F3] rounded-lg px-4 py-3 text-sm text-[#131B2F]"
                                data-testid={`availability-warning-row-${i}`}
                            >
                                <div className="flex items-center gap-2">
                                    <Icon size={14} strokeWidth={1.75} className="text-[#DA3535]" />
                                    <span className="text-[11px] uppercase tracking-wide text-[#DA3535]">
                                        {typeLabel(w.type)}
                                    </span>
                                </div>
                                <div className="font-medium mt-1">{humanReason(w)}</div>
                                {when && (
                                    <div className="text-[#4B5565] mt-1">{when}</div>
                                )}
                            </div>
                        );
                    })}
                </div>

                <div className="px-6 py-4 border-t border-[#E5E7EB] flex flex-wrap justify-end gap-2">
                    <button
                        onClick={onCancel}
                        data-testid="availability-warning-cancel"
                        className="inline-flex items-center gap-1.5 px-4 py-2 rounded-md border border-[#D1D5DB] text-sm font-medium text-[#131B2F] hover:bg-[#F5F5F7] transition-colors"
                    >
                        <ArrowLeft size={14} strokeWidth={1.75} /> Change booking
                    </button>
                    <button
                        onClick={onOverride}
                        data-testid="availability-warning-override"
                        className="inline-flex items-center gap-1.5 px-4 py-2 rounded-md bg-[#DA3535] text-white text-sm font-medium hover:bg-[#B92C2C] transition-colors"
                    >
                        Continue anyway <ArrowRight size={14} strokeWidth={1.75} />
                    </button>
                </div>
            </div>
        </div>
    );
}
